import type { AppendixData } from '@core/presentationPrint';
import { HeatOutputChart } from './HeatOutputChart';
import { ModulationCyclingChart } from './ModulationCyclingChart';
import { LongevityDiagram } from './LongevityDiagram';
import { ControlsModulationChart } from './ControlsModulationChart';
import { PipeworkFlowChart } from './PipeworkFlowChart';
import { AppendixExplainer, HeatPumpEnergyFlow } from '@decision-ui/components';
import { EnergyLossesTable } from './EnergyLossesTable';
import { EnergyLossesGraphic } from './EnergyLossesGraphic';
import { ElectricitySourcesGrid } from './ElectricitySourcesGrid';

interface AppendixPageProps {
  data: AppendixData;
  index: number;
}

const APPENDIX_ICONS: Record<string, string> = {
  'how-systems-stay-efficient': '⚙️',
  'power-flush-filters': '🧼',
  'why-design-affects-longevity': '🔧',
  'system-layout-pipework': '🛣️',
  'where-energy-is-lost': '🔥',
  'heat-pump-electricity-source': '⚡',
};

/**
 * One A4 appendix page (max 1 page each).
 * Icon-led, no numbers, no brands.
 * Now supports 5 appendices including energy losses and heat pump electricity.
 */
export function AppendixPage({ data }: AppendixPageProps) {
  const icon = APPENDIX_ICONS[data.id] || '📄';

  return (
    <div className="dp-page dp-appendix-page">
      {/* Optional reading label */}
      <div className="dp-appendix-optional-label">
        Optional background reading
      </div>
      
      <header className="dp-appendix-header">
        <span className="dp-appendix-icon" aria-hidden="true">
          {icon}
        </span>
        <h2>{data.title}</h2>
      </header>

      <div className="dp-appendix-body">
        {/* For Appendix 1: How Systems Stay Efficient (merged: clean systems + controls) */}
        {data.id === 'how-systems-stay-efficient' && (
          <>
            {/* Intro paragraph */}
            {data.content.length > 0 && (
              <p className="dp-appendix-paragraph">
                {data.content[0]}
              </p>
            )}

            {/* Graphic 1: Modulation vs. Cycling */}
            <figure
              className="dp-appendix-chart dp-appendix-figure"
              style={{ display: 'block', position: 'static', margin: '16px 0', pageBreakInside: 'avoid' }}
            >
              <ModulationCyclingChart width={560} height={260} />
            </figure>

            {/* Second paragraph */}
            {data.content.length > 1 && (
              <p className="dp-appendix-paragraph">
                {data.content[1]}
              </p>
            )}

            {/* Graphic 2: Longevity Diagram */}
            <figure
              className="dp-appendix-chart dp-appendix-figure"
              style={{ display: 'block', position: 'static', margin: '16px 0', pageBreakInside: 'avoid' }}
            >
              <LongevityDiagram width={560} height={180} />
            </figure>

            {/* Controls & Modulation content */}
            {data.content.slice(2).map((paragraph, i) => (
              <p key={i + 2} className="dp-appendix-paragraph">
                {paragraph}
              </p>
            ))}
          </>
        )}

        {/* For Appendix 2: Why Design Affects Longevity (merged: pipework + stress) */}
        {data.id === 'why-design-affects-longevity' && (
          <>
            {/* First paragraph */}
            {data.content.length > 0 && (
              <p className="dp-appendix-paragraph">
                {data.content[0]}
              </p>
            )}

            {/* Graphic 1: Pipework Flow */}
            <figure
              className="dp-appendix-chart dp-appendix-figure"
              style={{ display: 'block', position: 'static', margin: '16px 0', pageBreakInside: 'avoid' }}
            >
              <PipeworkFlowChart width={560} height={280} />
            </figure>

            {/* Second paragraph */}
            {data.content.length > 1 && (
              <p className="dp-appendix-paragraph">
                {data.content[1]}
              </p>
            )}

            {/* Third paragraph (stress concept) */}
            {data.content.length > 2 && (
              <p className="dp-appendix-paragraph">
                {data.content[2]}
              </p>
            )}

            {/* Graphic 2: Heat Output Chart */}
            <figure
              className="dp-appendix-chart dp-appendix-figure"
              style={{ display: 'block', position: 'static', margin: '16px 0', pageBreakInside: 'avoid' }}
            >
              <HeatOutputChart width={560} height={280} />
            </figure>

            {/* Final paragraph */}
            {data.content.slice(3).map((paragraph, i) => (
              <p key={i + 3} className="dp-appendix-paragraph">
                {paragraph}
              </p>
            ))}
          </>
        )}

        {/* For Appendix 3: System Layout and Pipe Sizing (motorway analogy) */}
        {data.id === 'system-layout-pipework' && (
          <AppendixExplainer printMode={true} />
        )}

        {/* For Appendix 4: Where Energy is Lost */}
        {data.id === 'where-energy-is-lost' && (
          <>
            {/* Intro paragraph */}
            {data.content.length > 0 && (
              <p className="dp-appendix-paragraph">
                {data.content[0]}
              </p>
            )}

            {/* Energy Losses Table */}
            <figure
              className="dp-appendix-chart dp-appendix-figure"
              style={{ display: 'block', position: 'static', margin: '16px 0', pageBreakInside: 'avoid' }}
            >
              <EnergyLossesTable />
            </figure>

            {/* Note below table */}
            {data.content.length > 1 && (
              <p className="dp-appendix-paragraph" style={{ fontSize: '12px', fontStyle: 'italic', marginTop: '8px' }}>
                {data.content[1]}
              </p>
            )}

            {/* Energy Losses Graphic */}
            <figure
              className="dp-appendix-chart dp-appendix-figure"
              style={{ display: 'block', position: 'static', margin: '16px 0', pageBreakInside: 'avoid' }}
            >
              <EnergyLossesGraphic width={560} height={280} />
            </figure>

            {/* Caption already in the graphic, but keep for accessibility */}
            {data.content.length > 2 && (
              <p className="dp-appendix-paragraph" style={{ fontSize: '11px', fontStyle: 'italic', color: '#64748b', textAlign: 'center', marginTop: '4px' }}>
                {data.content[2]}
              </p>
            )}
          </>
        )}

        {/* For Appendix 5: Heat Pump Electricity Source */}
        {data.id === 'heat-pump-electricity-source' && (
          <>
            {/* Intro paragraph */}
            {data.content.length > 0 && (
              <p className="dp-appendix-paragraph">
                {data.content[0]}
              </p>
            )}

            <figure
              className="dp-appendix-chart dp-appendix-figure"
              style={{ display: 'block', position: 'static', margin: '16px 0', pageBreakInside: 'avoid' }}
            >
              <HeatPumpEnergyFlow animated={false} printMode={true} />
            </figure>

            {/* Section heading */}
            <h3 style={{ fontSize: '14px', fontWeight: 600, marginTop: '16px', marginBottom: '12px' }}>
              Electricity Sources
            </h3>

            {/* Electricity Sources Grid */}
            <figure
              className="dp-appendix-chart dp-appendix-figure"
              style={{ display: 'block', position: 'static', margin: '16px 0', pageBreakInside: 'avoid' }}
            >
              <ElectricitySourcesGrid width={560} height={200} />
            </figure>

            {/* Why This Matters section */}
            <h3 style={{ fontSize: '14px', fontWeight: 600, marginTop: '16px', marginBottom: '8px' }}>
              Why This Matters
            </h3>
            {data.content.length > 1 && (
              <p className="dp-appendix-paragraph">
                {data.content[1]}
              </p>
            )}

            {/* Tariff Awareness section */}
            <h3 style={{ fontSize: '14px', fontWeight: 600, marginTop: '16px', marginBottom: '8px' }}>
              Tariff Awareness
            </h3>
            {data.content.length > 2 && (
              <p className="dp-appendix-paragraph">
                {data.content[2]}
              </p>
            )}

            {/* Future-Proofing line */}
            {data.content.length > 3 && (
              <p className="dp-appendix-paragraph" style={{ fontWeight: 500, marginTop: '12px', padding: '12px', background: '#f0f9ff', borderLeft: '3px solid #2563eb', borderRadius: '0 4px 4px 0' }}>
                {data.content[3]}
              </p>
            )}
          </>
        )}

        {/* For other appendices: Default rendering logic */}
        {data.id !== 'how-systems-stay-efficient' &&
         data.id !== 'why-design-affects-longevity' &&
         data.id !== 'system-layout-pipework' &&
         data.id !== 'where-energy-is-lost' &&
         data.id !== 'heat-pump-electricity-source' && (
          <>
            {/* All paragraphs */}
            {data.content.map((paragraph, i) => (
              <p key={i} className="dp-appendix-paragraph">
                {paragraph}
              </p>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
